import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../services/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import useAdminStore from '../../store/adminStore';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const UserCreateModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation('admin');
  const { authToken } = useAuth();
  const { setLoading, setError, addUser } = useAdminStore();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'free',
    status: 'active',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await adminApi.createUser({
        token: authToken,
        userData: formData,
      });

      if (result.success) {
        addUser(result.user);
        onSuccess?.();
        onClose();
        setFormData({
          name: '',
          email: '',
          password: '',
          role: 'free',
          status: 'active',
        });
      } else {
        setError(result.error || t('userCreateModal.createFailedFallback'));
      }
    } catch (error) {
      setError(getApiErrorMessage(error, { fallback: t('userCreateModal.genericErrorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100000]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[100001] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md border border-white/10 dark:border-white/10">
              <div className="flex items-center justify-between p-6 border-b border-white/10 dark:border-white/10">
                <h2 className="text-xl font-bold text-[var(--color-text-primary)] dark:text-white">
                  {t('userCreateModal.title')}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('userCreateModal.fullNameLabel')}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('userCreateModal.emailLabel')}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('userCreateModal.passwordLabel')}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('userCreateModal.roleLabel')}
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                  >
                    <option value="free">{t('userCreateModal.roles.free')}</option>
                    <option value="plus">{t('userCreateModal.roles.plus')}</option>
                    <option value="admin">{t('userCreateModal.roles.admin')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('userCreateModal.statusLabel')}
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--color-bg-base)] dark:bg-[#161f31] border border-white/10 dark:border-white/10 rounded-lg text-[var(--color-text-primary)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                  >
                    <option value="active">{t('userCreateModal.statuses.active')}</option>
                    <option value="inactive">{t('userCreateModal.statuses.inactive')}</option>
                  </select>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-[var(--color-text-secondary)] dark:text-gray-300 hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] rounded-lg transition-colors"
                  >
                    {t('userCreateModal.cancel')}
                  </button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center space-x-2 px-4 py-2 bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-lg font-medium transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    <span>{t('userCreateModal.create')}</span>
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default UserCreateModal;

